import type { DateRange } from '@heidi/design-system/date-range-picker';
import type { ClinicalTaskCategory } from '@heidi/schema/ml-scribe';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import { getSafeLocalStorage } from '@heidi/utils/jotai-storage';
import { getCurrentUser, getWorkspaceId } from '@heidi/workspace/data';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { AssignmentFilter, type ClinicalTask, type SortState } from '../contracts';
import {
  buildClinicalTasksStorageScope,
  CLINICAL_TASKS_ASSIGNMENT_FILTER_KEY,
  CLINICAL_TASKS_CATEGORY_FILTER_KEY,
  CLINICAL_TASKS_DUE_DATE_PRESET_KEY,
  CLINICAL_TASKS_DUE_DATE_RANGE_KEY,
  CLINICAL_TASKS_SORT_KEY,
  CLINICAL_TASKS_STATUS_FILTER_V2_KEY,
  type ClinicalTasksStorageBase,
  type ClinicalTasksStorageIdentity,
  createClinicalTasksScopedStorage,
  dateRangeReviver,
} from '../contracts/clinical-tasks-storage';

/**
 * THE STORAGE VOCABULARY AND ITS SCOPING RULE LIVE IN `../contracts/
 * clinical-tasks-storage.ts` NOW, and this module re-exports them so nothing
 * that imported them from here has to change (ruling 40 §6, on the programme
 * owner's severance ruling). Every key below is a re-export, not a second
 * declaration: `persistence-manifest.json` pins the strings, and two
 * declarations would let them drift silently.
 *
 * The reason for the split is that `src/utils/auth/logoutStorage.ts` needs the
 * prefix and the scope, `logoutStorage` is reachable from `pages/_app`, and
 * reaching them through this module dragged the whole `.` UI door into that
 * graph. The contracts module imports nothing, so the logout path now reaches
 * nothing.
 */
export {
  CLINICAL_TASKS_ASSIGNMENT_FILTER_KEY,
  CLINICAL_TASKS_CATEGORY_FILTER_KEY,
  CLINICAL_TASKS_DUE_DATE_PRESET_KEY,
  CLINICAL_TASKS_DUE_DATE_RANGE_KEY,
  CLINICAL_TASKS_SORT_KEY,
  CLINICAL_TASKS_STATUS_FILTER_KEY,
  CLINICAL_TASKS_STATUS_FILTER_V2_KEY,
  CLINICAL_TASKS_STORAGE_KEY_PREFIX,
  dateRangeReviver,
} from '../contracts/clinical-tasks-storage';

// TODO: refactor the filters
// interface ClinicalTasksFilters {
//   searchTerm: string;
//   inputValue: string;
//   statusFilter: ClinicalTaskStatus | 'all';
//   categoryFilter: ClinicalTaskCategory | 'all';
//   dateRange: DateRange;
//   datePreset: string | null;
//   datePickerOpen: boolean;
//   page: number;
//   pageSize: number;
// };

// const defaultFilters: ClinicalTasksFilters = {
//   searchTerm: '',
//   inputValue: '',
//   statusFilter: ClinicalTaskStatus.todo,
//   categoryFilter: 'all',
//   dateRange: { from: undefined, to: undefined },
//   datePreset: null,
//   datePickerOpen: false,
//   page: 1,
//   pageSize: 12,
// }

// export const clinicalTasksFiltersAtom = atom<ClinicalTasksFilters>(defaultFilters);

/**
 * THE APP-BOUND IDENTITY READER — the half the contracts module cannot hold,
 * and the only thing this split actually left behind here.
 *
 * `@heidi/workspace/data` is a foreign domain door, which `/contracts` may not
 * import and a `/ui` zone may (workspace is the sole FOUNDATIONAL domain). So
 * the rule lives there and the readers live here, and this thunk is the seam
 * between them. It is a thunk rather than a value because the scope resolves
 * LATE: an adapter is built once at module scope and read long afterwards, by
 * which time an unresolved workspace or user has arrived.
 */
const readClinicalTasksIdentity = (): ClinicalTasksStorageIdentity => ({
  workspaceId: getWorkspaceId(),
  userId: getCurrentUser()?.kinde_user_id,
});

/**
 * The zero-argument scope reader every existing caller already has. Its BODY
 * moved to `../contracts/clinical-tasks-storage`; what stays here is the
 * binding of that rule to this app's identity readers, which is the one thing
 * an inert door cannot do for itself.
 *
 * `src/utils/auth/logoutStorage.ts` no longer calls THIS — it composes the
 * contracts function with identity it resolves itself, because it must not
 * reach a `/ui` module (ruling 40 §6). Its own suite still computes the
 * expected scope through this binding, which makes the two paths' agreement an
 * assertion rather than an assumption.
 */
export function getClinicalTasksStorageScope(): string | null {
  return buildClinicalTasksStorageScope(readClinicalTasksIdentity());
}

/**
 * The default-argument adapter factory every existing caller already has
 * (`useHydrateClinicalTaskPrefs` calls it with none). Body in the contracts
 * module; the `getSafeLocalStorage()` default and the identity binding are
 * what stay here, both for the same reason — neither specifier is importable
 * from `/contracts`.
 */
export function createScopedTaskStorageAdapter(
  base: ClinicalTasksStorageBase = getSafeLocalStorage()
): ClinicalTasksStorageBase {
  return createClinicalTasksScopedStorage(base, readClinicalTasksIdentity);
}

function createScopedTaskStorage<T>(options?: {
  reviver?: (key: string, value: unknown) => unknown;
}) {
  return createJSONStorage<T>(() => createScopedTaskStorageAdapter(), options);
}

export const clinicalTasksSidePanelOpenAtom = atom<boolean>(false);

export const clinicalTasksSearchTermAtom = atom<string>('');
export const clinicalTasksInputValueAtom = atom<string>('');
export const clinicalTasksStatusFilterAtom = atom<ClinicalTaskStatus | 'all'>(
  ClinicalTaskStatus.todo
);
export const clinicalTasksStatusFilterAtomV2 = atomWithStorage<
  ClinicalTaskStatus | 'all'
>(
  CLINICAL_TASKS_STATUS_FILTER_V2_KEY,
  ClinicalTaskStatus.todo,
  createScopedTaskStorage<ClinicalTaskStatus | 'all'>()
);
export const clinicalTasksSortAtom = atomWithStorage<SortState>(
  CLINICAL_TASKS_SORT_KEY,
  null,
  createScopedTaskStorage<SortState>()
);
export const clinicalTasksCategoryFilterAtom = atomWithStorage<
  ClinicalTaskCategory | 'all'
>(
  CLINICAL_TASKS_CATEGORY_FILTER_KEY,
  'all',
  createScopedTaskStorage<ClinicalTaskCategory | 'all'>()
);
// V1-only "created date" filter — intentionally left unpersisted, unlike its
// due-date counterpart below.
export const clinicalTasksDateRangeAtom = atom<DateRange>({
  from: undefined,
  to: undefined,
});
export const clinicalTasksDatePresetAtom = atom<string | null>(null);
export const clinicalTasksDatePickerOpenAtom = atom<boolean>(false);
export const clinicalTasksDueDateRangeAtom = atomWithStorage<DateRange>(
  CLINICAL_TASKS_DUE_DATE_RANGE_KEY,
  { from: undefined, to: undefined },
  createScopedTaskStorage<DateRange>({ reviver: dateRangeReviver })
);
export const clinicalTasksDueDatePresetAtom = atomWithStorage<string | null>(
  CLINICAL_TASKS_DUE_DATE_PRESET_KEY,
  null,
  createScopedTaskStorage<string | null>()
);
export const clinicalTasksDueDatePickerOpenAtom = atom<boolean>(false);
export const clinicalTasksPageAtom = atom<number>(1);
export const clinicalTasksPageSizeAtom = atom<number>(10);
export const clinicalTasksAssignmentFilterAtom = atomWithStorage<AssignmentFilter>(
  CLINICAL_TASKS_ASSIGNMENT_FILTER_KEY,
  AssignmentFilter.all,
  createScopedTaskStorage<AssignmentFilter>()
);

export const clinicalTasksCreateInProgressAtom = atom<boolean>(false);
export const clinicalTasksCurrentClinicalTaskAtom = atom<ClinicalTask | null>(null);
export const clinicalTasksTaskSheetOpenAtom = atom<boolean>(false);
export const clinicalTasksActiveSheetTypeAtom = atom<'edit' | 'create' | null>(null);
// Transient deeplink carrier — set before navigating to /tasks so task_id
// never appears in the URL (and therefore never reaches analytics).
export const clinicalTasksPendingOpenTaskIdAtom = atom<string | null>(null);

export type ActionType = 'delete' | 'complete' | null;

// Linked-call confirmation modal state
export const clinicalTasksLinkedCallConfirmAtom = atom<{
  isOpen: boolean;
  action: ActionType;
  task: ClinicalTask | null;
  onAfterConfirm?: (() => void) | null;
}>({
  isOpen: false,
  action: null,
  task: null,
  onAfterConfirm: null,
});
